import { useDatoClient } from '@src/stores-v2/StoreContext';
import { LogManager } from '@src/utility/Logger';
import { reaction } from 'mobx';
import { useCallback, useEffect, useRef, useState } from 'react';

import { composeDubTrack } from './composeDubTrack';

import type { ResolvedDubManifest } from '@src/repositories/DubbingRepository';
import type { ITranscriptVideoCreator } from '@src/stores/ITranscriptVideoCreator';

const logger = LogManager.getLogger('aiVoices:useDubPlayback');

// How far the composed dub may drift from the shared clock before we treat a
// `time` change as a seek and restart the buffer at the new offset (seconds).
const SEEK_RESYNC_THRESHOLD = 0.3;

/**
 * What a story surface says when the dub cannot be played.
 *
 * Every host mutes the video element while the voice is set to Dubbed, so a
 * track that fails to build is not merely a missing dub — it is total silence,
 * with the picture running and nothing coming out.
 */
const DUB_TRACK_FAILED_MESSAGE =
  'The dubbed audio could not be loaded, so there is nothing to play. Reload the page and try again.';
/** The dub itself still plays, so this is a warning rather than an error. */
const MUSIC_BED_FAILED_MESSAGE =
  'The music could not be loaded. The dubbed voice is playing without it.';

/**
 * Play a composed dub track over the (muted) original video, synced to the
 * shared transcript clock (PLAT-10597, DE4 live-audio path). When `active`
 * (audio mode = Dubbed) and a translated language is selected, it fetches the
 * dub manifest (PLAT-10619), composes a single story-length buffer
 * (`composeDubTrack`), then follows `videoCreator.isPlaying` / `videoCreator.time`:
 * start on play, stop on pause, and restart at the new offset on a seek. The
 * caller mutes the video element so only the dub is heard.
 *
 * When `playMusicBed` is set and `musicBedUrl` is a kept music bed (PLAT-10626),
 * a **second** Web Audio source plays that bed through a `GainNode` in lockstep
 * with the dub — so the translated voice sits over the original music instead of
 * dry air. The bed shares the dub's context/offset/resync, and can be toggled
 * on/off mid-playback without disturbing the dub.
 *
 * Returns the composed dub `AudioBuffer` (PLAT-10620) so a surface can derive
 * the dubbed waveform from the exact audio being played; `null` until it's
 * composed / when inactive.
 *
 * `prepare` (PLAT-10620) decouples *composing* the track from *playing* it: pass
 * it true as soon as the selected language has a dub (before the user toggles to
 * Dubbed) so the buffer + waveform are ready instantly on toggle. Composition is
 * driven by `active || prepare`, so flipping `active` alone never recomposes.
 */
export function useDubPlayback(params: {
  videoCreator: ITranscriptVideoCreator;
  storyId: string | null;
  /** BCP-47 code of the selected language; `null` = Original (no dub). */
  language: string | null;
  /** True when the audio mode is Dubbed (drives playback). */
  active: boolean;
  /** Compose the track ahead of playback (e.g. a dubbed language is selected). */
  prepare?: boolean;
  /**
   * Bump to force a manifest re-fetch + recompose (PLAT-10616). The manifest is
   * otherwise fetched only when story/language change, so a re-dub of the
   * current language would keep playing the stale composed buffer until a page
   * reload. The host increments this on every successful dub generate/re-dub.
   */
  reloadToken?: number;
  /**
   * Signed URL of the kept music bed (PLAT-10626); `null` when there is none.
   * Decoded ahead of playback whenever `active || prepare`.
   */
  musicBedUrl?: string | null;
  /** Play the music bed under the dub (Music = Original, voice = Dubbed). */
  playMusicBed?: boolean;
  /**
   * PLAT-11899 — the dubbed voice's level, 0–100 (default 100). Applied live
   * through the voice's own gain, so moving it mid-playback never restarts
   * the dub.
   */
  voiceVolume?: number;
  /**
   * PLAT-11906 — mix only the lines the clip audio places, never a superseded
   * copy on top of its newer recording (`composeDubTrack`). The Translation
   * Review asks for it, so what it plays matches the captions and highlights it
   * times to the same dub; other surfaces keep the mix they had.
   */
  placedLinesOnly?: boolean;
}): {
  dubBuffer: AudioBuffer | null;
  /**
   * PLAT-11906 — the dub description the track was composed from, so a
   * surface can time captions and highlights to the same dub without a second
   * fetch; `null` until fetched, and whenever the track is not being composed.
   */
  resolved: ResolvedDubManifest | null;
} {
  const {
    videoCreator,
    storyId,
    language,
    active,
    prepare = false,
    reloadToken = 0,
    musicBedUrl = null,
    playMusicBed = false,
    voiceVolume = 100,
    placedLinesOnly = false,
  } = params;
  const datoClient = useDatoClient();
  // Compose when either playing or pre-warming; toggling `active` alone must not
  // re-run the compose effect (else the prepared buffer is thrown away).
  const shouldCompose = active || prepare;
  // Read the live `active` inside effects without depending on it.
  const activeRef = useRef(active);
  activeRef.current = active;
  const playMusicBedRef = useRef(playMusicBed);
  playMusicBedRef.current = playMusicBed;
  const voiceGainValue = Math.min(100, Math.max(0, voiceVolume)) / 100;
  const voiceGainValueRef = useRef(voiceGainValue);
  voiceGainValueRef.current = voiceGainValue;

  const ctxRef = useRef<AudioContext | null>(null);
  // Lazily create (and reuse) the shared AudioContext — the dub source and the
  // music bed both hang off it, and both effects may be the first to need it.
  const getOrCreateCtx = useCallback((): AudioContext => {
    const ctx =
      ctxRef.current ??
      new (
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext
      )();
    ctxRef.current = ctx;
    return ctx;
  }, []);
  // What the compose effect last failed with, and which failure the person has
  // already been told about.
  //
  // Held rather than shown on the spot because the effect is deliberately not
  // keyed on `active`: a failure met while only pre-warming must stay quiet,
  // nobody being listening yet, but it must not be forgotten either, or
  // switching to Dubbed afterwards delivers silence with the reason already
  // thrown away. Keyed on story, language and reload token together, so one
  // failure is reported once however often the effect re-runs for something
  // else.
  const composeFailureRef = useRef<{ key: string; message: string } | null>(
    null,
  );
  const reportedFailureKeyRef = useRef<string | null>(null);
  const reportedMusicBedRef = useRef<string | null>(null);
  const composeKey = `${storyId ?? ''}|${language ?? ''}|${reloadToken}`;

  const reportComposeFailure = useCallback(() => {
    const failure = composeFailureRef.current;
    if (!failure) return;
    if (reportedFailureKeyRef.current === failure.key) return;
    reportedFailureKeyRef.current = failure.key;
    videoCreator.showToast({ state: 'error', message: failure.message });
  }, [videoCreator]);

  const bufferRef = useRef<AudioBuffer | null>(null);
  // State mirror of bufferRef so consumers (the waveform) re-render when the
  // composed track becomes available (PLAT-10620).
  const [dubBuffer, setDubBuffer] = useState<AudioBuffer | null>(null);
  // The description the track was composed from, with the story, language and
  // reload token it was fetched for. It is handed out only while those are
  // still the ones asked for, so on the render where the language changes the
  // previous language's description is never returned, not even once; a re-run
  // of the compose effect for another reason keeps it.
  const [held, setHeld] = useState<{
    key: string;
    resolved: ResolvedDubManifest;
  } | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const startCtxTimeRef = useRef(0);
  const startOffsetRef = useRef(0);

  // The kept music bed (PLAT-10626) — a second source through its own gain.
  const musicBufferRef = useRef<AudioBuffer | null>(null);
  const musicSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const musicGainRef = useRef<GainNode | null>(null);
  // PLAT-11899 — the dubbed voice's own gain, created with the first source.
  const voiceGainRef = useRef<GainNode | null>(null);

  const stopMusicSource = useCallback(() => {
    const src = musicSourceRef.current;
    if (src) {
      try {
        src.stop();
      } catch {
        // already stopped
      }
      src.disconnect();
      musicSourceRef.current = null;
    }
  }, []);

  const stopSource = useCallback(() => {
    const src = sourceRef.current;
    if (src) {
      try {
        src.stop();
      } catch {
        // already stopped
      }
      src.disconnect();
      sourceRef.current = null;
    }
    stopMusicSource();
  }, [stopMusicSource]);

  const startMusicAt = useCallback(
    (timeSec: number) => {
      const ctx = ctxRef.current;
      const buffer = musicBufferRef.current;
      if (!ctx || !buffer) return;
      stopMusicSource();
      const offset = Math.max(0, timeSec);
      if (offset >= buffer.duration) return;
      let gain = musicGainRef.current;
      if (!gain) {
        gain = ctx.createGain();
        gain.gain.value = 1; // full-level bed under the dub (ducking is a later lever)
        gain.connect(ctx.destination);
        musicGainRef.current = gain;
      }
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(gain);
      src.start(0, offset);
      musicSourceRef.current = src;
    },
    [stopMusicSource],
  );

  const startAt = useCallback(
    (timeSec: number) => {
      const ctx = ctxRef.current;
      if (!ctx) return;
      stopSource();
      if (ctx.state === 'suspended') void ctx.resume();
      const offset = Math.max(0, timeSec);
      startCtxTimeRef.current = ctx.currentTime;
      startOffsetRef.current = offset;
      const buffer = bufferRef.current;
      // Start the dub if it has audio at this offset (silence/gaps are baked in).
      if (buffer && offset < buffer.duration) {
        let voiceGain = voiceGainRef.current;
        if (!voiceGain) {
          voiceGain = ctx.createGain();
          voiceGain.connect(ctx.destination);
          voiceGainRef.current = voiceGain;
        }
        voiceGain.gain.value = voiceGainValueRef.current;
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        src.connect(voiceGain);
        src.start(0, offset);
        sourceRef.current = src;
      }
      // Start the kept bed in lockstep when Music = Original.
      if (playMusicBedRef.current) startMusicAt(offset);
    },
    [stopSource, startMusicAt],
  );

  // Fetch + compose whenever the language/story changes or we start needing the
  // track (play or pre-warm). Not keyed on `active` so a Dubbed toggle on an
  // already-prepared track is instant.
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    bufferRef.current = null;
    setDubBuffer(null);
    setHeld(previous =>
      shouldCompose && previous?.key === composeKey ? previous : null,
    );
    // A failure belongs to the story, language and reload token it was met on,
    // and must not be carried into the next one.
    composeFailureRef.current = null;
    stopSource();
    if (!shouldCompose || !storyId || !language) return;

    void (async () => {
      try {
        const ctx = getOrCreateCtx();
        const resolved = await datoClient.dubbingRepository.getDubManifest({
          storyId,
          language,
          signal: controller.signal,
        });
        if (cancelled) return;
        if (!resolved) {
          // Not a failure to announce (PLAT-11704): a language with no dub is
          // an ordinary state, and every host drops its Voice back to Original
          // the moment its dubbed-language read says so — the picture keeps
          // its own voice and music. A sentence here would tell a translator
          // who never asked for a dub to go and make one.
          logger.warn('No dub manifest for the language being played', {
            storyId,
            language,
          });
          return;
        }
        setHeld(previous =>
          previous?.key === composeKey && previous.resolved === resolved
            ? previous
            : { key: composeKey, resolved },
        );
        const buffer = await composeDubTrack(ctx, resolved, controller.signal, {
          placedLinesOnly,
        });
        if (cancelled) return;
        bufferRef.current = buffer;
        setDubBuffer(buffer);
        if (!buffer) {
          // A null track means not one line decoded, which is what an expired
          // signature on the stored audio looks like.
          composeFailureRef.current = {
            key: composeKey,
            message: DUB_TRACK_FAILED_MESSAGE,
          };
          if (activeRef.current) reportComposeFailure();
        }
        // If we're actively playing when the track becomes ready, join it (but
        // never start audio while only pre-warming in Original mode).
        if (buffer && activeRef.current && videoCreator.isPlaying)
          startAt(videoCreator.time);
      } catch (err) {
        if (cancelled) return;
        logger.warn('Dub track load failed', { err });
        composeFailureRef.current = {
          key: composeKey,
          message: DUB_TRACK_FAILED_MESSAGE,
        };
        if (activeRef.current) reportComposeFailure();
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      stopSource();
    };
  }, [
    shouldCompose,
    storyId,
    language,
    composeKey,
    reportComposeFailure,
    // Re-fetch + recompose after a dub generate/re-dub so the current language's
    // updated audio plays without a page reload (PLAT-10616).
    reloadToken,
    placedLinesOnly,
    datoClient,
    videoCreator,
    startAt,
    stopSource,
    getOrCreateCtx,
  ]);

  // A failure met while pre-warming is reported the moment the person switches
  // the voice to Dubbed. The compose effect above is deliberately not keyed on
  // `active`, so without this the switch delivers silence and says nothing.
  useEffect(() => {
    if (!active) return;
    reportComposeFailure();
  }, [active, reportComposeFailure]);

  // Decode the kept music bed once its signed URL is available (PLAT-10626).
  // Keyed ONLY on the bed URL — NOT on the voice axis (`active`/`prepare`). The
  // bed is language-agnostic (one per story) and independent of Dubbed/Original,
  // so keying it on the voice mode made every voice toggle (and translation
  // on/off) tear down the decoded buffer and re-fetch; rapid toggles aborted the
  // in-flight fetch, leaving the bed silent until a page reload decoded it
  // cleanly (the "no music until refresh" bug). Playback start/stop is owned by
  // `startAt` and the `playMusicBed` toggle effect, not this decode.
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    musicBufferRef.current = null;
    stopMusicSource();
    if (!musicBedUrl) return;

    void (async () => {
      try {
        const ctx = getOrCreateCtx();
        const resp = await fetch(musicBedUrl, { signal: controller.signal });
        const bytes = await resp.arrayBuffer();
        if (cancelled) return;
        const buffer = await ctx.decodeAudioData(bytes);
        if (cancelled) return;
        musicBufferRef.current = buffer;
        // If Original is already on and we're playing, join in sync with the dub.
        if (
          playMusicBedRef.current &&
          activeRef.current &&
          videoCreator.isPlaying
        ) {
          const expected =
            startOffsetRef.current +
            (ctx.currentTime - startCtxTimeRef.current);
          startMusicAt(expected);
        }
      } catch (err) {
        if (cancelled) return;
        logger.warn('Music bed load failed', { err });
        // Only when the person would actually be hearing it: the bed is decoded
        // ahead of time and independently of the voice, so a failure while it is
        // switched off is nothing they can act on.
        if (
          activeRef.current &&
          playMusicBedRef.current &&
          reportedMusicBedRef.current !== musicBedUrl
        ) {
          reportedMusicBedRef.current = musicBedUrl;
          videoCreator.showToast({
            state: 'warning',
            message: MUSIC_BED_FAILED_MESSAGE,
            delay: 6000,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      stopMusicSource();
    };
  }, [
    musicBedUrl,
    videoCreator,
    startMusicAt,
    stopMusicSource,
    getOrCreateCtx,
  ]);

  // Follow the shared clock while active. On activation, join in-progress
  // playback immediately so a Dubbed toggle mid-play swaps audio at once.
  useEffect(() => {
    if (!active) return undefined;
    if (videoCreator.isPlaying) startAt(videoCreator.time);
    const disposePlaying = reaction(
      () => videoCreator.isPlaying,
      playing => {
        if (playing) startAt(videoCreator.time);
        else stopSource();
      },
    );
    const disposeSeek = reaction(
      () => videoCreator.time,
      t => {
        const ctx = ctxRef.current;
        if (
          !videoCreator.isPlaying ||
          !(bufferRef.current || musicBufferRef.current) ||
          !ctx
        )
          return;
        // When a source is live, only restart if the clock has drifted past the
        // resync threshold. When there's no live source (e.g. playback started
        // past the dub's end, then seeked back into range), let `startAt` decide
        // — it restarts if in range, no-ops if still past the end.
        if (sourceRef.current || musicSourceRef.current) {
          const expected =
            startOffsetRef.current +
            (ctx.currentTime - startCtxTimeRef.current);
          if (Math.abs(t - expected) <= SEEK_RESYNC_THRESHOLD) return;
        }
        startAt(t);
      },
    );
    return () => {
      disposePlaying();
      disposeSeek();
      stopSource();
    };
  }, [active, videoCreator, startAt, stopSource]);

  // Toggle the music bed on/off mid-playback without disturbing the dub source.
  // Keyed on `playMusicBed` only — activation/seek/play are owned by `startAt`.
  useEffect(() => {
    if (!playMusicBed) {
      stopMusicSource();
      return;
    }
    const ctx = ctxRef.current;
    if (!activeRef.current || !ctx || !videoCreator.isPlaying) return;
    const expected =
      startOffsetRef.current + (ctx.currentTime - startCtxTimeRef.current);
    startMusicAt(expected);
  }, [playMusicBed, videoCreator, startMusicAt, stopMusicSource]);

  // PLAT-11899 — a level change reaches the voice that is already playing.
  useEffect(() => {
    if (voiceGainRef.current) voiceGainRef.current.gain.value = voiceGainValue;
  }, [voiceGainValue]);

  // Close the audio context on unmount.
  useEffect(
    () => () => {
      stopSource();
      musicGainRef.current?.disconnect();
      musicGainRef.current = null;
      voiceGainRef.current?.disconnect();
      voiceGainRef.current = null;
      void ctxRef.current?.close();
      ctxRef.current = null;
    },
    [stopSource],
  );

  return {
    dubBuffer,
    resolved: shouldCompose && held?.key === composeKey ? held.resolved : null,
  };
}
